import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';
import { offPlatformBillingBlockedMessage, preferShopifyAppBilling } from '../utils/shopifyBillingRail';
import { billingService, BillingInfo, BillingSummary, UsageStats } from '../services/billingService';
import { subscriptionService, UserSubscription } from '../services/subscriptionService';
import { paymentService, PaymentMethod, Invoice } from '../services/paymentService';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import EmailService from '../services/emailService';
import { userService } from '../services/firestore';
import { invoiceService } from '../services/invoiceService';

// Types for billing context
interface BillingContextType {
  // Billing data
  billingInfo: BillingInfo | null;
  billingSummary: BillingSummary | null;
  usageStats: UsageStats | null;

  // Loading states
  loading: boolean;
  billingLoading: boolean;
  subscriptionLoading: boolean;

  // Error states
  error: string | null;

  // Actions
  refreshBillingInfo: () => Promise<void>;
  refreshUsageStats: () => Promise<void>;
  updateSubscription: (
    plan: string,
    subscribers: number,
    currency: string,
    billingPeriod?: 'monthly' | 'annual',
    paymentAmount?: number,
    taxAmount?: number,
    taxRate?: number,
    paymentId?: string,
    paymentGateway?: 'stripe' | 'razorpay' | 'dodo'
  ) => Promise<void>;
  cancelSubscription: (immediately?: boolean) => Promise<void>;
  addPaymentMethod: (paymentMethodId: string) => Promise<void>;
  removePaymentMethod: (paymentMethodId: string) => Promise<void>;
  setDefaultPaymentMethod: (paymentMethodId: string) => Promise<void>;
  resetSubscriptionDates: () => Promise<void>;

  // Utility functions
  canPerformAction: (action: 'send_email' | 'add_subscriber') => Promise<boolean>;
  needsPaymentUpdate: () => Promise<boolean>;
  getUpcomingCharges: () => Promise<any>;
  isShopifyWorkspace: boolean;
  /** True when app charges must go through Shopify (Admin ?shop= or connected Shopify integration). */
  preferShopifyBilling: boolean;
  initiateShopifyBilling: (
    planTier: 'pro' | 'business',
    subscribers: number,
    billingPeriod?: 'monthly' | 'annual'
  ) => Promise<void>;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export const useBilling = () => {
  const context = useContext(BillingContext);
  if (context === undefined) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
};

interface BillingProviderProps {
  children: ReactNode;
}

export const BillingProvider: React.FC<BillingProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentWorkspace, isShopifyEmbedded } = useWorkspace();
  const location = useLocation();

  // State
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [billingLoading, setBillingLoading] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isShopifyWorkspace, setIsShopifyWorkspace] = useState(false);
  const [profileBillingSource, setProfileBillingSource] = useState<'shopify' | 'direct' | null>(null);
  const [profileAcquisitionChannel, setProfileAcquisitionChannel] = useState<string | null>(null);

  // Detect Shopify workspace on mount or when current workspace changes
  useEffect(() => {
    const checkShopifyIntegration = async () => {
      if (!currentUser || !currentWorkspace?.id) {
        setIsShopifyWorkspace(false);
        return;
      }
      
      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const integrationsRef = collection(db, 'userIntegrations');
        const q = query(
          integrationsRef,
          where('userId', '==', currentUser.uid),
          where('workspaceId', '==', currentWorkspace.id),
          where('status', '==', 'connected')
        );
        const querySnapshot = await getDocs(q);
        const hasShopify = querySnapshot.docs.some((docSnap) => {
          const row = docSnap.data() as { slug?: string; integrationId?: string };
          return row.slug === 'shopify' || row.integrationId === 'shopify';
        });
        setIsShopifyWorkspace(hasShopify);
        console.log(`[BillingContext] Shopify integration for ${currentWorkspace.id}:`, hasShopify);
      } catch (err) {
        console.error('Error checking Shopify integration:', err);
      }
    };

    checkShopifyIntegration();
  }, [currentUser, currentWorkspace?.id]);

  useEffect(() => {
    const loadBillingSource = async () => {
      if (!currentUser) {
        setProfileBillingSource(null);
        setProfileAcquisitionChannel(null);
        return;
      }
      try {
        const profile = await userService.getUserProfile(currentUser.uid);
        setProfileBillingSource(profile?.billingSource ?? null);
        setProfileAcquisitionChannel(profile?.acquisitionChannel ?? null);
      } catch {
        setProfileBillingSource(null);
        setProfileAcquisitionChannel(null);
      }
    };
    void loadBillingSource();
  }, [currentUser?.uid]);

  const preferShopifyBilling = useMemo(
    () =>
      preferShopifyAppBilling({
        locationSearch: location.search,
        isShopifyEmbedded,
        billingSource: profileBillingSource,
        acquisitionChannel: profileAcquisitionChannel,
        hasShopifyIntegration: isShopifyWorkspace,
      }),
    [
      location.search,
      isShopifyEmbedded,
      profileBillingSource,
      profileAcquisitionChannel,
      isShopifyWorkspace,
    ]
  );

  // Load billing information
  const loadBillingInfo = async () => {
    if (!currentUser) {
      setBillingInfo(null);
      setBillingSummary(null);
      setUsageStats(null);
      setLoading(false);
      return;
    }

    try {
      setBillingLoading(true);
      setError(null);

      const [billingInfoData, billingSummaryData, usageStatsData] = await Promise.all([
        billingService.getBillingInfo(currentUser.uid),
        billingService.getBillingSummary(currentUser.uid),
        billingService.getUsageStats(currentUser.uid),
      ]);

      setBillingInfo(billingInfoData);
      setBillingSummary(billingSummaryData);
      setUsageStats(usageStatsData);
    } catch (err) {
      console.error('Error loading billing info:', err);
      setError(err instanceof Error ? err.message : 'Failed to load billing information');
    } finally {
      setBillingLoading(false);
      setLoading(false);
    }
  };

  // Refresh billing information
  const refreshBillingInfo = async () => {
    await loadBillingInfo();
  };

  // Refresh usage stats
  const refreshUsageStats = async () => {
    if (!currentUser) return;

    try {
      setError(null);
      const stats = await billingService.getUsageStats(currentUser.uid);
      setUsageStats(stats);
    } catch (err) {
      console.error('Error refreshing usage stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh usage stats');
    }
  };

  // Update subscription
  const updateSubscription = async (
    plan: string,
    subscribers: number,
    currency: string,
    billingPeriod: 'monthly' | 'annual' = 'monthly',
    paymentAmount?: number,
    taxAmount?: number,
    taxRate?: number,
    paymentId?: string,
    paymentGateway?: 'stripe' | 'razorpay' | 'dodo'
  ) => {
    console.log('BillingContext: updateSubscription called with', {
      plan,
      subscribers,
      currency,
      billingPeriod,
      paymentAmount,
      paymentId,
      paymentGateway
    });

    if (!currentUser) {
      console.log('BillingContext: No current user');
      throw new Error('User not authenticated');
    }

    // Shopify App Store: paid plan changes from Admin or Shopify-linked workspace use Billing API only.
    const paidTier = plan === 'business' ? 'business' : plan === 'pro' ? 'pro' : null;
    if (preferShopifyBilling && paidTier) {
      console.log('BillingContext: Shopify billing rail — paid plan change');
      return await initiateShopifyBilling(paidTier, subscribers, billingPeriod);
    }
    if (preferShopifyBilling && (paymentGateway === 'dodo' || paymentGateway === 'stripe' || paymentGateway === 'razorpay')) {
      throw new Error(offPlatformBillingBlockedMessage());
    }

    const inferredGateway: 'stripe' | 'razorpay' | 'dodo' | undefined =
      paymentGateway ||
      (paymentId?.startsWith('pay_') ? 'razorpay' : paymentId?.startsWith('pi_') ? 'stripe' : undefined);
    const paymentMeta =
      inferredGateway || paymentId
        ? {
            ...(inferredGateway ? { paymentProvider: inferredGateway } : {}),
            ...(paymentId ? { lastPaymentReference: paymentId } : {})
          }
        : undefined;

    try {
      setSubscriptionLoading(true);
      setError(null);

      // Check if user already has a subscription
      console.log('BillingContext: Checking for existing subscription');
      const existingSubscription = await subscriptionService.getUserSubscription(currentUser.uid);
      console.log('BillingContext: Existing subscription:', existingSubscription);

      let updatedSubscription;
      if (existingSubscription) {
        // Update existing subscription - reset period dates since this is a new payment
        console.log('BillingContext: Updating existing subscription with new payment dates');
        updatedSubscription = await subscriptionService.updateSubscriptionPlan(
          currentUser.uid,
          plan,
          subscribers,
          currency,
          billingPeriod,
          true,
          paymentMeta
        );
      } else {
        // Create new subscription
        console.log('BillingContext: Creating new subscription');
        updatedSubscription = await subscriptionService.createSubscription(
          currentUser.uid,
          plan,
          subscribers,
          currency,
          billingPeriod,
          paymentMeta
        );
      }

      // Create invoice record for paid plans (using actual payment amount)
      if ((plan === 'pro' || plan === 'business') && paymentAmount && paymentAmount > 0) {
        try {
          const { billingService } = await import('../services/billingService');
          await billingService.createLocalInvoice(
            currentUser.uid,
            updatedSubscription,
            paymentAmount,
            currency,
            paymentId
          );
          console.log('BillingContext: Invoice created for payment amount:', paymentAmount);
        } catch (invoiceError) {
          console.warn('Failed to create invoice record:', invoiceError);
          // Don't fail subscription creation if invoice creation fails
        }
      }

      console.log('BillingContext: Subscription updated, refreshing billing info');
      await loadBillingInfo(); // Refresh billing info after update
      console.log('BillingContext: Billing info refreshed');

      // Send plan activation / update email only after registration milestone (dashboard + onboarding emails)
      try {
        const userProfile = await userService.getUserProfile(currentUser.uid);
        if (!userProfile?.registrationEmailsSentAt) {
          console.log(
            'BillingContext: Skipping plan activation email until registrationEmailsSentAt is set'
          );
        }
        const updatedSubscription = await subscriptionService.getUserSubscription(currentUser.uid);
        if (userProfile?.registrationEmailsSentAt && updatedSubscription && currentUser.email) {
          // Use actual payment amount if provided, otherwise try upcomingCharges as fallback
          let amount = paymentAmount;
          if (amount === undefined || amount === null) {
            const upcomingCharges = await billingService.getUpcomingCharges(currentUser.uid);
            amount = upcomingCharges?.amount ?? 0;
          }

          // Use the currency that was passed to this function (actual payment currency)
          const currencyCode = currency.toLowerCase();
          const planName = updatedSubscription.plan === 'starter' ? 'Starter (Free)' :
            updatedSubscription.plan === 'business' ? 'Business' : 'Pro';

          // Calculate correct dates - use actual subscription dates from the service (already converted to Date objects)
          const startDate = updatedSubscription.currentPeriodStart;
          const endDate = updatedSubscription.currentPeriodEnd;

          // Generate invoice PDF and upload to Firebase Storage
          let invoiceDownloadUrl: string | undefined;
          try {
            const invoiceId = invoiceService.generateInvoiceId(currentUser.uid);
            const invoiceResult = await invoiceService.generateAndUploadInvoice({
              invoiceId,
              userId: currentUser.uid,
              userName: currentUser.displayName || currentUser.email.split('@')[0],
              userEmail: currentUser.email,
              planName,
              amount,
              currency: currencyCode,
              billingPeriod,
              paymentDate: startDate,
              periodStart: startDate,
              periodEnd: endDate,
              subscribers,
              taxAmount,
              taxRate,
              paymentId,
            });

            if (invoiceResult.success && invoiceResult.downloadUrl) {
              invoiceDownloadUrl = invoiceResult.downloadUrl;
              console.log('📄 Invoice PDF generated:', invoiceDownloadUrl);
            }
          } catch (invoiceError) {
            console.warn('BillingContext: Failed to generate invoice PDF:', invoiceError);
            // Continue without invoice - email will link to billing page instead
          }

          await EmailService.sendPlanActivationEmail({
            to: currentUser.email,
            userName: currentUser.displayName || currentUser.email.split('@')[0],
            planName,
            amount,
            currency: currencyCode,
            billingPeriod: billingPeriod,
            startDate: startDate,
            endDate: endDate,
            invoiceDownloadUrl,
          });
        }
      } catch (emailError) {
        console.warn('BillingContext: Failed to send plan activation email:', emailError);
        // Do not throw here; subscription update succeeded
      }
    } catch (err) {
      console.error('BillingContext: Error updating subscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to update subscription');
      throw err;
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Initiate Shopify Billing
  const initiateShopifyBilling = async (
    planTier: 'pro' | 'business',
    subscribers: number,
    billingPeriod: 'monthly' | 'annual' = 'monthly'
  ) => {
    try {
      setSubscriptionLoading(true);
      setError(null);
      
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const createShopifySubscription = httpsCallable(functions, 'createShopifySubscription');

      let workspaceId = currentWorkspace?.id || '';
      if (!workspaceId && currentUser?.uid) {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const integrationsRef = collection(db, 'userIntegrations');
        const q = query(
          integrationsRef,
          where('userId', '==', currentUser.uid),
          where('status', '==', 'connected')
        );
        const querySnapshot = await getDocs(q);
        const match = querySnapshot.docs.find((docSnap) => {
          const row = docSnap.data() as { slug?: string; integrationId?: string; workspaceId?: string };
          return (row.slug === 'shopify' || row.integrationId === 'shopify') && row.workspaceId;
        });
        if (match) {
          workspaceId = (match.data() as { workspaceId?: string }).workspaceId || '';
        }
      }

      const billingTestFlag =
        process.env.REACT_APP_SHOPIFY_BILLING_TEST === 'true' ||
        process.env.REACT_APP_SHOPIFY_BILLING_TEST === '1';

      const result = (await createShopifySubscription({
        workspaceId: workspaceId || undefined,
        planTier,
        subscribers,
        billingPeriod,
        isTest: billingTestFlag,
      })) as any;

      if (result.data?.success && result.data?.confirmationUrl) {
        // Redirect to Shopify's confirmation URL.
        // Use window.top when inside Shopify Admin iframe; fall back if cross-origin access is blocked.
        try {
          if (window.top) {
            window.top.location.href = result.data.confirmationUrl;
          } else {
            window.location.href = result.data.confirmationUrl;
          }
        } catch {
          // Cross-origin security restriction — fall back to current window
          window.location.href = result.data.confirmationUrl;
        }
      } else {
        throw new Error('Failed to get Shopify confirmation URL');
      }
    } catch (err) {
      console.error('Error initiating Shopify billing:', err);
      setError('Failed to initiate Shopify billing. Please ensure you are opening the app from Shopify Admin.');
      throw err;
    } finally {
      setSubscriptionLoading(false);
    }
  };


  // Cancel subscription
  const cancelSubscription = async (immediately: boolean = false) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      setSubscriptionLoading(true);
      setError(null);

      await subscriptionService.cancelSubscription(currentUser.uid, immediately);
      await loadBillingInfo(); // Refresh billing info after cancellation
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel subscription');
      throw err;
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Add payment method
  const addPaymentMethod = async (paymentMethodId: string) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      setBillingLoading(true);
      setError(null);

      await billingService.addPaymentMethod(currentUser.uid, paymentMethodId);
      await loadBillingInfo(); // Refresh billing info after adding payment method
    } catch (err) {
      console.error('Error adding payment method:', err);
      setError(err instanceof Error ? err.message : 'Failed to add payment method');
      throw err;
    } finally {
      setBillingLoading(false);
    }
  };

  // Remove payment method
  const removePaymentMethod = async (paymentMethodId: string) => {
    try {
      setBillingLoading(true);
      setError(null);

      await billingService.removePaymentMethod(paymentMethodId);
      await loadBillingInfo(); // Refresh billing info after removing payment method
    } catch (err) {
      console.error('Error removing payment method:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove payment method');
      throw err;
    } finally {
      setBillingLoading(false);
    }
  };

  // Set default payment method
  const setDefaultPaymentMethod = async (paymentMethodId: string) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      setBillingLoading(true);
      setError(null);

      await billingService.setDefaultPaymentMethod(currentUser.uid, paymentMethodId);
      await loadBillingInfo(); // Refresh billing info after setting default payment method
    } catch (err) {
      console.error('Error setting default payment method:', err);
      setError(err instanceof Error ? err.message : 'Failed to set default payment method');
      throw err;
    } finally {
      setBillingLoading(false);
    }
  };

  // Reset subscription dates to today
  const resetSubscriptionDates = async () => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      setSubscriptionLoading(true);
      setError(null);

      await subscriptionService.resetSubscriptionDatesToToday(currentUser.uid);
      await loadBillingInfo(); // Refresh billing info after resetting dates
    } catch (err) {
      console.error('Error resetting subscription dates:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset subscription dates');
      throw err;
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Check if user can perform an action
  const canPerformAction = async (action: 'send_email' | 'add_subscriber'): Promise<boolean> => {
    if (!currentUser) return false;

    try {
      return await subscriptionService.canPerformAction(currentUser.uid, action);
    } catch (err) {
      console.error('Error checking action permission:', err);
      return false;
    }
  };

  // Check if user needs payment update
  const needsPaymentUpdate = async (): Promise<boolean> => {
    if (!currentUser) return false;

    try {
      return await billingService.needsPaymentMethodUpdate(currentUser.uid);
    } catch (err) {
      console.error('Error checking payment update need:', err);
      return false;
    }
  };

  // Get upcoming charges
  const getUpcomingCharges = async () => {
    if (!currentUser) return null;

    try {
      return await billingService.getUpcomingCharges(currentUser.uid);
    } catch (err) {
      console.error('Error getting upcoming charges:', err);
      return null;
    }
  };

  // Load billing info when user changes
  useEffect(() => {
    loadBillingInfo();
  }, [currentUser]);

  // Set up real-time listener for subscription changes
  useEffect(() => {
    if (!currentUser) return;

    // Debounce the callback 400 ms — the onSnapshot fires on mount (initial
    // read) right after the [currentUser] useEffect already called loadBillingInfo.
    // Without the debounce both triggers run simultaneously, causing a double
    // load on every login.
    let debounceTimer: ReturnType<typeof setTimeout>;

    const userRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const userData = snapshot.data();
        if (userData.subscription) {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            loadBillingInfo();
          }, 400);
        }
      }
    }, (error) => {
      console.error('Error listening to subscription changes:', error);
    });

    return () => {
      clearTimeout(debounceTimer);
      unsubscribe();
    };
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Context value — memoized so consumers only re-render when data actually changes
  const contextValue: BillingContextType = useMemo(() => ({
    billingInfo,
    billingSummary,
    usageStats,
    loading,
    billingLoading,
    subscriptionLoading,
    error,
    refreshBillingInfo,
    refreshUsageStats,
    updateSubscription,
    cancelSubscription,
    addPaymentMethod,
    removePaymentMethod,
    setDefaultPaymentMethod,
    resetSubscriptionDates,
    canPerformAction,
    needsPaymentUpdate,
    getUpcomingCharges,
    isShopifyWorkspace,
    preferShopifyBilling,
    initiateShopifyBilling,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [billingInfo, billingSummary, usageStats, loading, billingLoading, subscriptionLoading, error]);

  return (
    <BillingContext.Provider value={contextValue}>
      {children}
    </BillingContext.Provider>
  );
};

